// Story 056 — Histogram widget (blown-out detection). 256-bin luminance
// distribution of the CURRENT image (the same activation the Filmstrip
// publishes via `imageActivationStore` — Story 011), with optional R/G/B
// channel overlays, a log/linear y-axis flip, and clipping labels when
// more than 1 % of sampled pixels sit in bucket 0 / bucket 255.
//
// The chart is a raw <canvas> (fixed 256 bins — recharts would be pure
// overhead); the pixel bucketing runs in a Web Worker off a 256 × 256
// downsample (see `useImageHistogram`). Channel/log state persists via
// `histogramWidgetStore` (localStorage `aero.lyse.analysis.histogram.v1`).
import { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ImageRef } from "@aeriosiq/types";
import { compactToggleXsSx, typography } from "@aeriosiq/ui";
import { useImagesQuery } from "../../queries/useImagesQuery.js";
import { useImageActivationStore } from "../../state/imageActivationStore.js";
import {
  clippingPercentages,
  isClipped,
  HISTOGRAM_BINS,
  type HistogramResult,
} from "../../histogram/computeHistogram.js";
import { useImageHistogram } from "../../histogram/useImageHistogram.js";
import {
  useHistogramWidgetStore,
  type HistogramChannel,
  type HistogramChannels,
} from "../../histogram/histogramWidgetStore.js";

// Channel trace colours — colorimetric identities (the R trace IS red),
// not theme decisions. Drawn from the same Apple-system palette the
// severity scales already use (see SEVERITY_BBOX_FALLBACK / SketchWidget)
// so they read consistently against both modes.
const CHANNEL_COLOR: Record<HistogramChannel, string> = {
  r: "#FF3B30",
  g: "#34C759",
  b: "#0A84FF",
};

// Internal canvas resolution — 2 device px per bin keeps the trace crisp
// while the element itself scales to the widget cell via CSS.
const CANVAS_W = HISTOGRAM_BINS * 2;
const CANVAS_H = 160;

/** Format a clipping share for display: one decimal, e.g. "70.3". */
function formatPct(pct: number): string {
  return pct.toFixed(1);
}

/** Human description of the drawn traces, for the chart's accessible name. */
function traceDescription(channels: HistogramChannels): string {
  const on = (["r", "g", "b"] as const).filter((c) => channels[c]);
  if (on.length === 0) return "luminance";
  const names = on.map((c) => c.toUpperCase()).join(", ");
  // All three channel overlays on → the luminance trace is dropped
  // (story Scope: "toggles drop the L trace if all three of R/G/B are on").
  return on.length === 3 ? `${names} channels` : `luminance + ${names}`;
}

interface HistogramChartProps {
  histogram: HistogramResult;
  channels: HistogramChannels;
  logScale: boolean;
}

function HistogramChart({
  histogram,
  channels,
  logScale,
}: HistogramChartProps): React.ReactElement {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const showLuminance = !(channels.r && channels.g && channels.b);
  const luminanceColor = theme.palette.text.secondary;

  useEffect(() => {
    const canvas = canvasRef.current;
    // jsdom (component tests) has no 2D context — the paint is skipped,
    // the semantics (role/name/labels) are what those tests assert.
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const traces: { values: Uint32Array; color: string; fill: boolean }[] = [];
    if (showLuminance) {
      traces.push({ values: histogram.l, color: luminanceColor, fill: true });
    }
    for (const c of ["r", "g", "b"] as const) {
      if (channels[c]) {
        traces.push({
          values: histogram[c],
          color: CHANNEL_COLOR[c],
          fill: false,
        });
      }
    }

    let max = 0;
    for (const trace of traces) {
      for (const v of trace.values) max = Math.max(max, v);
    }
    // Log scale lifts thin bins when one bucket dominates (story AC6);
    // log1p keeps zero-count bins at exactly zero height.
    const yOf = (count: number): number => {
      if (max === 0) return CANVAS_H;
      const share = logScale
        ? Math.log1p(count) / Math.log1p(max)
        : count / max;
      return CANVAS_H - share * CANVAS_H;
    };

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    const binW = CANVAS_W / HISTOGRAM_BINS;
    for (const trace of traces) {
      ctx.beginPath();
      ctx.moveTo(0, CANVAS_H);
      for (let bin = 0; bin < HISTOGRAM_BINS; bin += 1) {
        ctx.lineTo((bin + 0.5) * binW, yOf(trace.values[bin] ?? 0));
      }
      ctx.lineTo(CANVAS_W, CANVAS_H);
      if (trace.fill) {
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = trace.color;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.strokeStyle = trace.color;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [histogram, channels, logScale, showLuminance, luminanceColor]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      role="img"
      aria-label={`Histogram for the current image — ${String(HISTOGRAM_BINS)} bins, ${traceDescription(channels)}`}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  );
}

interface HistogramWidgetProps {
  dataAcquisitionId: string;
}

export function HistogramWidget({
  dataAcquisitionId,
}: HistogramWidgetProps): React.ReactElement {
  const imagesQuery = useImagesQuery(dataAcquisitionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const activeImage: ImageRef | null = useMemo(() => {
    if (!activeImageId) return null;
    const items = imagesQuery.data?.items ?? [];
    return items.find((it) => it.imageId === activeImageId) ?? null;
  }, [activeImageId, imagesQuery.data?.items]);

  const { status, histogram } = useImageHistogram(activeImage);

  const channels = useHistogramWidgetStore((s) => s.channels);
  const logScale = useHistogramWidgetStore((s) => s.logScale);
  const toggleChannel = useHistogramWidgetStore((s) => s.toggleChannel);
  const setLogScale = useHistogramWidgetStore((s) => s.setLogScale);

  const clipping = histogram ? clippingPercentages(histogram.l) : null;

  return (
    <Stack sx={{ height: "100%", p: 1, gap: 0.75 }}>
      {/* Channel toggles + log/linear flip — one dense control row
          (SB6-ANA-UI-01 compact scale). */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        sx={{ columnGap: 1, rowGap: 0.5, flexShrink: 0 }}
      >
        <FormGroup row sx={{ columnGap: 0.5 }}>
          {(["r", "g", "b"] as const).map((channel) => (
            <FormControlLabel
              key={channel}
              sx={{ mr: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={channels[channel]}
                  onChange={() => {
                    toggleChannel(channel);
                  }}
                  // Colour the GLYPH as the channel cue (≥ 3:1 against
                  // paper, WCAG 1.4.11); the 13-px text label stays
                  // text.primary so it clears the 4.5:1 text AA bar.
                  sx={{
                    p: 0.5,
                    color: CHANNEL_COLOR[channel],
                    "&.Mui-checked": { color: CHANNEL_COLOR[channel] },
                  }}
                />
              }
              label={channel.toUpperCase()}
              slotProps={{
                typography: {
                  sx: {
                    fontSize: typography.size["caption-lg"],
                    fontWeight: typography.weight.semibold,
                  },
                },
              }}
            />
          ))}
        </FormGroup>
        <ToggleButton
          value="log"
          size="small"
          selected={logScale}
          onChange={() => {
            setLogScale(!logScale);
          }}
          sx={compactToggleXsSx}
        >
          Log scale
        </ToggleButton>
      </Stack>

      {/* Chart body — states: empty (no image) / computing / failed / chart. */}
      <Box sx={{ flexGrow: 1, minHeight: 80, position: "relative" }}>
        {status === "idle" ? (
          <StateHint>No image selected.</StateHint>
        ) : status === "loading" ? (
          <StateHint>
            <CircularProgress size={14} sx={{ mr: 1 }} aria-hidden />
            Computing histogram…
          </StateHint>
        ) : status === "error" || !histogram ? (
          <StateHint>
            Couldn&apos;t compute a histogram for this image.
          </StateHint>
        ) : (
          <HistogramChart
            histogram={histogram}
            channels={channels}
            logScale={logScale}
          />
        )}
      </Box>

      {/* Clipping markers — real text (AC8), only when the 1 % threshold
          is crossed. */}
      {clipping && isClipped(clipping.highlightsPct) ? (
        <Typography
          variant="caption"
          sx={{ color: "text.primary", flexShrink: 0 }}
        >
          Highlights clipped: {formatPct(clipping.highlightsPct)} %
        </Typography>
      ) : null}
      {clipping && isClipped(clipping.shadowsPct) ? (
        <Typography
          variant="caption"
          sx={{ color: "text.primary", flexShrink: 0 }}
        >
          Shadows clipped: {formatPct(clipping.shadowsPct)} %
        </Typography>
      ) : null}
    </Stack>
  );
}

function StateHint({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%" }}
    >
      <Typography variant="caption" color="text.secondary" role="status">
        {children}
      </Typography>
    </Stack>
  );
}
